import { useEffect, useState } from "react";
import { ChevronRight, Vote } from "lucide-react";
import { toast } from "sonner";

import { Chip, Loading, PageHeader, Panel } from "@/components/common/Chrome";
import { VotePaper } from "@/components/governance/VotePaper";
import { api, apiError } from "@/lib/api";

const pretty = (s) => String(s || "").slice(0, 16).replace("T", " ");

export default function VotePage() {
  const [items, setItems] = useState(null);
  const [picked, setPicked] = useState("");

  const load = async () => {
    try {
      const res = await api.get("/elections/voting/live");
      setItems(res.data.items);
      setPicked((p) => p || res.data.items[0]?.election_ref || "");
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  if (!items) return <Loading label="Looking for anything you can vote in" />;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Governance · Phase 4" title="Vote"
                  description="Mark your paper, keep it private for as long as you like, and cast it when you are ready. Your name is kept apart from your choices — the box shows turnout only." />

      {items.length === 0 ? (
        <Panel title="Nothing to vote in" testId="panel-no-vote">
          <p className="text-[12.5px] text-muted-foreground" data-testid="no-live-votes">
            There is no election open for voting at the moment. You will be told when one opens.
          </p>
        </Panel>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
          <Panel title="Open to you" testId="panel-live-votes">
            <div className="space-y-1.5">
              {items.map((e) => (
                <button key={e.election_ref} type="button"
                        data-testid={`live-vote-${e.election_ref}`}
                        onClick={() => setPicked(e.election_ref)}
                        className={`flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-left transition-colors duration-150 ${
                          picked === e.election_ref
                            ? "border-primary/50 bg-primary/5" : "border-border hover:border-primary/30"}`}>
                  <span>
                    <span className="block text-[12.5px] font-medium text-foreground">
                      {e.name}
                    </span>
                    <span className="block text-[10.5px] text-muted-foreground">
                      {e.status === "voting_open" ? `closes ${pretty(e.closes_at)}`
                        : `opens ${pretty(e.opens_at)}`}
                    </span>
                  </span>
                  {e.you_have_voted
                    ? <Chip className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">voted</Chip>
                    : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                </button>
              ))}
            </div>
            <p className="mt-3 flex items-start gap-1.5 text-[10.5px] leading-relaxed text-muted-foreground">
              <Vote className="mt-0.5 h-3 w-3" />
              One member, one vote. A cast paper cannot be changed.
            </p>
          </Panel>
          {picked && <VotePaper key={picked} electionRef={picked} onChanged={load} />}
        </div>
      )}
    </div>
  );
}
